/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./FilterBaseController',
	'./NonNumericFilterView',
	'./NonNumericFilterModel',
	'../../features/dashboard/queryService/QueryServiceUtils',
	'underscore'
], function(BaseClass, FilterView, FilterModel, QueryServiceUtils, _) {
	'use strict';

	/**
	 * The controller holds the business logic for the range filter
	 */
	var NonNumericFilterController = BaseClass.extend({

		init: function() {
			NonNumericFilterController.inherited('init', this, arguments);
			this.id = _.uniqueId('NonNumericFilterController');
			this.itemContext.eventSourceId = this.id;
			this.ready = Promise.resolve(null);
			this.resetSearch = false;
			this.lastFilterCondition = null;
			this.searchString = null;
		},

		initialize: function() {
			this._initializeExtraData();
			this._createFilterModel();
			this._createFilterView();
			this.setLoading();
			this._loadColumnData()
				.then(this.onInitializationDone.bind(this));
			return Promise.resolve();
		},

		_initializeExtraData: function() {
			this._initializePageContext( /*isRange*/ false);
		},

		_loadColumnData: function() {
			return this._getColumnData()
				.then( members => {
					return this._loadAllSelectedMembersFromPageContext(members, this._getFilteredValues())
						.then(list => {
							this.model.set({
								list: list
							});
							this._refreshModel();
						});
				})
				.catch(e => {
					this.processError(e);
				});
		},

		_createFilterModel: function() {
			var modelOptions = {
				name: this.itemContext.itemName,
				list: []
			};
			this.model = new FilterModel(modelOptions);

			var pageContextItem = this.getCurrentPageContextItem();
			if (pageContextItem) {
				this.model.setName(pageContextItem.getItemName());
				this.model.setInvert(pageContextItem.getExclude());
				this.model.setIsToggled(this._initialToggle(pageContextItem));
				this._refreshModel();
			}
		},

		_initialToggle: function(pageContextItem) {
			return (pageContextItem.getValues() && pageContextItem.getValues().length === 0) && !pageContextItem.getExclude();
		},

		_createFilterView: function() {
			this.viewConfig = _.extend(this.viewConfig, {
				model: this.model,
				showCondition: this.getShowCondition(),
				showToggle: this.getShowToggle(),
				cbOnToggleChange: this.onToggleChange.bind(this),
				cbOnClick: this.onClick.bind(this),
				cbOnChange: this.onChange.bind(this),
				cbOnClear: this.onClear.bind(this),
				cbOnDone: this.onDone.bind(this),
				cbOnApply: this.onApply.bind(this),
				cbOnClose: this.onClose.bind(this),
				cbOnSearch: this.onSearch.bind(this),
				cbGetValue: this.getValue.bind(this),
				cbGetInvertDisabled: this.getInvertDisabled.bind(this)
			});

			this.view = new FilterView(this.viewConfig);
		},

		getInvertDisabled: function () {
			let globalPageContextItem = this._getGlobalPageContextItem();
			if (globalPageContextItem && globalPageContextItem.getValueCount() && !globalPageContextItem.getExclude()) {
				let selectedValues = this.model.toJSON().selectedMembers;
				let diffValue = _.find(globalPageContextItem.getValues(), tuple => { return !selectedValues[tuple.u]; });
				return diffValue === undefined;
			}
			return false;
		},

		onOpen: function() {
			this.setLoading();
			if (this._ensureFilterViewExists()) {
				// New view or model is created.
				this.view.renderViewComposition()
					.then(this._loadColumnData.bind(this))
					.then(this.removeLoading.bind(this));
			} else {
				const refresh = () => {
					this._refreshModel({ silent: true });
					this.removeLoading();
				};
				// [188729] clear old search result state to prevent stale state from corrupting the new view
				if (this.searchString && this.searchString.length > 0) {
					this.onSearch('').then(refresh);
				} else {
					refresh();
				}
			}
		},

		/**
		 * Invoked when the following value changes.
		 */
		onClick: function(payload) {
			// Update model
			this.model.toggleSelectState(payload.id);
			// Update the toggle state if the toggle is shown
			if (this.getShowToggle()) {
				var currentValues = this.model.toJSON();
				var isToggled = _.size(currentValues.selectedMembers) === 0;
				if (isToggled !== this.model.getIsToggled()) {
					this.onToggleChange(isToggled);
				}
			}
		},

		onToggleChange: function(isToggled) {
			this.model.setIsToggled(isToggled);
			if (isToggled) {
				this.model.clearValues();
			}
			this.view.doRender();
		},

		/**
		 * Invoked when the following value changes.
		 */
		onChange: function(payload) {
			this.model.setCondition(payload.condition);

			// if no text entered in FC, clear
			if (payload.condition && payload.condition.predicates[0].value === '') {
				this.clearFilterCondition();
				return Promise.resolve(null);
			}

			// if in compact mode w/no condition,
			// OR, if the only thing that changed is the state of the invert flag, don't requery, just invert
			var onlyChangedInvert = (this.model.getInvert() !== payload.inverted) && _.isEqual(this.lastFilterCondition, payload.condition);

			if (!_.has(payload, 'condition') || onlyChangedInvert) {
				if (this.searchString) {
					this.model.toggleSelectStateAll();
				} else {
					this.model.setInvert(payload.inverted);
					this.model.applyChangeInvert();
				}
				return Promise.resolve(null);
			} else {
				//The user hit 'apply' in the condition dialog.
				if (!payload.condition) {
					//the user hit 'Clear condition', then apply....no query needed.
					this.clearFilterCondition();
					return Promise.resolve(null);
				} else {
					//the user hit apply (usually making a change to the conditions).....reexecute the query.
					//NOTE: even if the condition is unchanged, reexecute to recheck old unchecked user overrides.
					// cache copy of filter condition
					this.setLoading();
					return this.applyFilterCondition(payload.condition)
						.then(this.removeLoading.bind(this));
				}
			}
		},

		applyFilterCondition: function(condition) {
			var clearInvert = (!_.isEqual(this.lastFilterCondition, condition)); //Clear the invert flag as well (unless the condition didn't change).
			this.setLastFilterCondition(condition);
			this.model.clearValues( /*clearinvert*/ clearInvert); //Clear old user overrides whenever the condition is re-rexecuted.
			return this._getColumnDataWithConditions(condition)
				.then(function(members) {
					this.model.clearCondition();
					this.model.setFilterConditionQueryResults(members);
					this.model.setCondition(condition);
					return Promise.resolve(null);
				}.bind(this))
				.catch(function(err) {
					return Promise.reject(err);
				});
		},

		clearFilterCondition: function() {
			// clear cached filter condition
			this.lastFilterCondition = null;
			this.model.clearCondition();
		},

		setLastFilterCondition: function(condition) {
			if (condition) {
				this.lastFilterCondition = JSON.parse(JSON.stringify(condition));
			}
		},

		onClear: function() {
			this.model.clearValues(/*clearInvert*/undefined, /*doClearAllAction*/true);
			this.clearFilterCondition();
		},

		onDone: function() {

			var useValueDataItem = this.metadataInfo.isNamedSet && _.size(this.model.selectedMembers) === 0;

			var filterSpec = {
				command: 'replace',
				values: this.model.selectedMembers
			};

			if (useValueDataItem) {
				filterSpec.valueDataItem = this.itemContext.itemId;
				filterSpec.exclude = false;
			} else {
				filterSpec.exclude = this.model.getInvert();
			}

			this.pageContextApi.updateFilterContext(this.itemContext, filterSpec);

			if (this.model.onDone) {
				this.model.onDone();
			}
			if (!this.view.isCompact) {
				this.view.remove();
			}
		},

		onSearch: function(payload) {
			// Tell the MUI component to render a 'searching' indicator
			this.model.set({ isSearching: true });
			this.searchString = payload;
			return this._getColumnData(payload)
				.then(function(members) {
					this.resetSearch = true;

					// set search members that are already selected to true
					_.each(members, function(member) {
						if (this.model.selectedMembers[member.u]) {
							member.selected = true;
						}
						if (this.model.filterConditionQueryResults[member.u]) {
							member.matchesCondition = true;
						}
					}.bind(this));
					this.model.set({
						list: members,
						isSearching: false
					});
				}.bind(this))
				.catch(function(e) {
					this.model.set({ isSearching: false });
					this.processError(e);
				}.bind(this));
		},

		/**
		 * Invoke when user click the 'Cancel' buttons
		 */
		onClose: function() {
			if (this.model.onClose) {
				this.model.onClose();
			}
			NonNumericFilterController.inherited('onClose', this, arguments);
		},

		getValue: function(rowIndex) {
			return this.model.getValue(rowIndex);
		},

		// to be overridden by child classes
		getShowCondition: function() {
			return false;
		},

		// Currently only show toggle button for namedSet.
		getShowToggle: function() {
			return this.metadataInfo.isNamedSet;
		},

		_getColumnData: function(searchTerm) {
			return this.queryHelper.getColumnData(this.scope, searchTerm);
		},

		_getColumnDataWithConditions: function(conditions) {
			return this.queryHelper.getColumnDataWithConditions(this.scope, conditions);
		},

		/**
		 * Commence the render sequence of the Member filter view.
		 *
		 * @param {object} [options] - options
		 * @param {string} [options.searchIcon] - search icon to use; if none provided, no icon will be displayed
		 *
		 * @return {Promise} Resolves when render sequence is complete.
		 */
		render: function(force) {
			// clear search results
			if (this.resetSearch || force) {
				this.setLoading();
				this._getColumnData()
					.then(members => {
						return this._loadAllSelectedMembersFromPageContext(members, this._getFilteredValues())
							.then(list => {
								this.removeLoading();
								this.resetSearch = false;
								this.model.set({
									list: list
								});
								this._refreshModel();
							});
					})
					.catch(e => {
						this.processError(e);
						return Promise.reject(e);
					});
			} else {
				this._refreshModel();
			}

			return this.view.renderViewComposition();
		},

		refreshDataAndView: function() {
			return this.render(true);
			//@todo The following incomplete code attempt to fix update filter context. Will go back afterwards
			// var oldSelectedMembers = this.model.selectedMembers;
			// this.model.clearValues();
			// return this.render(true).then(function() {
			// 	if (this._updateFilterContext(oldSelectedMembers)) {
			// 		this._updatePageFilterContext();
			// 	}
			// }.bind(this));
		},

		/**
		 * Checks and return if page context ned to update filter context
		 *
		 * @param {Array} oldSelectedMembers - previous selected members-
		 *
		 * @return {boolean} true if filter context needs to be updated else return false
		 */
		_updateFilterContext: function(oldSelectedMembers) {
			var firstSet, secondSet;
			var oldValues = _.pluck(oldSelectedMembers, 'u');
			var newValues = _.pluck(this.model.selectedMembers, 'u');

			if (oldValues.length > newValues.length) {
				firstSet = oldValues;
				secondSet = newValues;
			} else {
				firstSet = newValues;
				secondSet = oldValues;
			}
			return _.difference(firstSet, secondSet).length > 0;
		},

		/**
		 * This function is called by processContextChange if page context change impacts current filter.
		 * @return {Promise}
		 */
		_updateFilterDataOnPageContextChange: function() {
			return this._loadColumnData();
		},

		_refreshModel: function(options) {
			this.model.clearValues(true, false, options);
			// load currently selected members from pagecontext
			var pageContextItem = this.getCurrentPageContextItem();
			if (pageContextItem) {
				const itemValues = pageContextItem.getValues();
				this._addOrRemovePageContextPersistedList(itemValues);
				this.model.setSelectedMembers(itemValues, options);
				// load current invert state from pagecontext
				this.model.setInvert(pageContextItem.getExclude(), options);

				// Reset istoggled if the full set is filtered.
				if (this.view) {
					this.model.setIsToggled(pageContextItem.isValueDataItem());
				}
			} else {
				// toggle off if filter has been deleted
				this.model.setIsToggled(false);
			}
		},

		_addOrRemovePageContextPersistedList: function(itemValues) {
			const currentList = this.model.get('list') ? this.model.get('list').filter(cItem => cItem.hasOwnProperty('value')) : [];
			const persistedList = itemValues.filter(item => !currentList.find(cItem => cItem.u === item.u));
			const extendedList = persistedList.length > 0 ? [...currentList, ...persistedList] : currentList;
			this.model.set({
				list: extendedList
			});
		},

		_getFilteredValues: function() {
			var pageContextItem = this.getCurrentPageContextItem();
			return pageContextItem ? pageContextItem.getValues() : [];
		},

		/**
		* Addtional filered entries will be added to the list of values
		*
		* @param {array} itemsLoaded - the list of items already loaded
		*
		* @return {array} array of loaded items that contain all filter values from page context
		*/
		_loadAllSelectedMembersFromPageContext: function(itemsLoaded = [], values = []) {
			if (!this._maxItemsReached(itemsLoaded) || values.length === 0) {
				//No fitlered values stored in page context so return original list of items
				return Promise.resolve(itemsLoaded);
			}

			//convert currentList to map so it is easier to work with
			var itemsMap = {};
			_.each(itemsLoaded, function(item) {
				itemsMap[item.u] = item;
			});

			var filteredItemsToLoad = [];
			_.each(values, function(value) {
				if (!itemsMap[value.u]) {
					filteredItemsToLoad.push(value.d);
				}
			});

			if (filteredItemsToLoad.length > 0) {
				return this._getColumnData(filteredItemsToLoad)
					.then(results => {
						results = results || [];
						if(results.length > 0) {
							itemsLoaded = itemsLoaded.concat(results);
						}
						return itemsLoaded;
					});
			} else {
				return Promise.resolve(itemsLoaded);
			}
		},

		/**
		* Verify if the list has reached maximum entries allow.
		*
		* @param {array} listToVerify - the array to verify
		*
		* @return {boolean} true if list length has reached maximum entries allow or return false
		*/
		_maxItemsReached: function(listToVerify) {
			listToVerify = listToVerify || [];
			return listToVerify.length >= QueryServiceUtils.COLUMN_QUERY_SIZE_LIMIT;
		}
	});
	return NonNumericFilterController;
});